import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight, ArrowRight } from 'lucide-react';
import Particles from '@/components/sections/Particles';
import SectionMarker from '@/components/sections/SectionMarker';
import CompanyCard from '@/components/sections/CompanyCard';
import Timeline from '@/components/sections/Timeline';
import PullQuote from '@/components/sections/PullQuote';
import Vision from '@/components/sections/Vision';
import Ecosystem from '@/components/sections/Ecosystem';
import FoundersListForm from '@/components/sections/FoundersListForm';
import { COMPANIES, PHOTOS, STAN_STRATEGY_URL, RESOURCES_ITEMS } from '@/constants/content';
import { HOME, RESOURCES, JOURNAL as J_TID } from '@/constants/testIds';
import { useReveal } from '@/lib/useReveal';
import { usePageMeta } from '@/lib/usePageMeta';
import { SEO, personSchema } from '@/constants/seo';
import { digitalApi } from '@/lib/digitalApi';
import { formatMonthYear } from '@/constants/resourceSections';

export default function Home() {
  usePageMeta({ ...SEO.home, jsonLd: personSchema });
  const aboutRef = useReveal();
  const [latestJournal, setLatestJournal] = useState([]);
  useEffect(() => {
    digitalApi.list({ section: 'journal', content_type: 'article', limit: 3 }).then((d) => setLatestJournal(d.items)).catch(() => setLatestJournal([]));
  }, []);
  const heroRef = useReveal();

  return (
    <div className="bg-ivory">
      {/* HERO */}
      <section data-testid={HOME.hero} className="relative min-h-[100svh] pt-28 lg:pt-40 pb-16 overflow-hidden">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-sand/40 via-ivory to-ivory" />
          <Particles density={55} />
        </div>

        <div className="relative mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 items-center">
          <div ref={heroRef} className="reveal lg:col-span-6 pt-8 lg:pt-0">
            <div className="mb-8" data-testid="home-hero-origenne-signature">
              <div className="eyebrow !text-gold">ORIGENNE&trade;</div>
              <div className="eyebrow text-taupe mt-2">The Personal Founder HQ of Toia Wallace</div>
            </div>
            <h1 className="font-serif-display text-[13vw] sm:text-7xl lg:text-[6.5rem] xl:text-[7.5rem] leading-[0.94] tracking-tightest text-matte">
              Building companies
              <br />
              that help people
              <br />
              <em className="italic text-taupe">build better</em>
              <br />
              businesses.
            </h1>

            <div className="mt-10 max-w-lg">
              <div className="flex flex-wrap items-center gap-x-4 gap-y-2 mb-6">
                {['Founder', 'Technology Entrepreneur', 'AI Strategist', 'Speaker'].map((t) => (
                  <span key={t} className="font-label text-[10px] tracking-widest uppercase text-matte/70">
                    {t}
                    <span className="ml-4 inline-block w-1 h-1 rounded-full bg-gold align-middle last:hidden" />
                  </span>
                ))}
              </div>
              <p className="text-matte/70 leading-relaxed text-[15px]">
                I build technology, educational platforms, and AI-powered systems that help
                entrepreneurs launch, grow, and scale smarter.
              </p>
            </div>

            <div className="mt-12 flex flex-wrap gap-4">
              <Link to="/companies" data-testid={HOME.ctaExplore} className="btn-primary">
                Explore My Companies
                <ArrowRight size={14} strokeWidth={1.6} />
              </Link>
              <Link to="/founder-scorecard" data-testid="hero-scorecard-cta" className="btn-gold">
                Take the Founder Systems Scorecard
                <ArrowRight size={14} strokeWidth={1.6} />
              </Link>
              <a href={STAN_STRATEGY_URL} target="_blank" rel="noreferrer" data-testid={HOME.ctaSpeak} className="btn-secondary">
                Book a Strategy Intensive
              </a>
            </div>
          </div>

          <div className="lg:col-span-6 relative">
            <div className="relative rounded-3xl overflow-hidden luxury-shadow animate-fade-in">
              <img
                src={PHOTOS.homeHero}
                alt="Toia Wallace, founder"
                className="w-full h-[62vh] lg:h-[82vh] object-cover object-top"
                loading="eager"
              />
              <div className="absolute inset-0 pointer-events-none bg-gradient-to-t from-ivory/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 flex items-end justify-between text-white/90 font-label text-[10px] tracking-widest uppercase">
                <span>Toia Wallace</span>
                <span>Vol. 01 · Founder</span>
              </div>
            </div>
            <div className="hidden lg:block absolute -bottom-8 -left-8 h-40 w-40 rounded-full bg-gold/15 blur-3xl" aria-hidden />
          </div>
        </div>
      </section>

      {/* THE VISION, emotional anchor */}
      <Vision />

      {/* ABOUT PREVIEW */}
      <section id="about" ref={aboutRef} className="reveal py-32 lg:py-56 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-16 lg:gap-28 items-center">
          <div className="lg:col-span-5 order-2 lg:order-1">
            <div className="relative rounded-3xl overflow-hidden">
              <img src="https://customer-assets-m6fa6gv7.emergentagent.net/job_toia-ecosystem/artifacts/5j3zhoya_6IX_0610.webp" alt="Toia Wallace" className="w-full h-[70vh] object-cover" loading="lazy" />
            </div>
          </div>
          <div className="lg:col-span-7 order-1 lg:order-2" data-testid={HOME.aboutPreview}>
            <SectionMarker number="01" label="Meet Toia Wallace" />
            <h2 className="mt-12 font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-matte">
              A founder building the
              <br />
              <em className="italic text-taupe">next generation</em>
              <br />
              of companies.
            </h2>
            <div className="mt-12 max-w-xl space-y-8 text-matte/75 leading-[1.85] text-[16px]">
              <p>
                Toia Wallace is a founder, technology entrepreneur, and AI business
                transformation strategist. She designs and builds companies at the
                intersection of software, education, and modern lifestyle.
              </p>
              <p>
                Her work is deliberately quiet, an ecosystem of ventures that use
                technology and AI to give entrepreneurs a smarter way to build.
              </p>
            </div>
            <div className="mt-12">
              <Link to="/about" className="btn-ghost">
                Read the story
                <ArrowRight size={14} strokeWidth={1.6} />
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* THE FOUNDER ECOSYSTEM */}
      <Ecosystem />

      {/* COMPANIES */}
      <section id="companies" className="py-32 lg:py-56 bg-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20">
            <div>
              <SectionMarker number="03" label="Companies in Motion" />
              <h2 className="mt-10 font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none text-matte max-w-2xl">
                The portfolio.
              </h2>
            </div>
            <p className="text-matte/70 max-w-md leading-relaxed">
              A growing portfolio of ventures across technology, media, education, and real
              estate, each designed to help founders build with intention.
            </p>
          </div>

          <div data-testid={HOME.companiesGrid} className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {COMPANIES.slice(0, 6).map((c, i) => (
              <CompanyCard key={c.slug} company={c} index={i} />
            ))}
          </div>

          <div className="mt-16 flex justify-center">
            <Link to="/companies" className="btn-ghost">
              See all companies
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* SPEAKING */}
      <section className="py-32 lg:py-56 bg-matte text-ivory relative overflow-hidden">
        <div className="absolute inset-0 opacity-25 noise-grain pointer-events-none" aria-hidden />
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-16 lg:gap-24 items-center relative">
          <div className="lg:col-span-6">
            <div className="eyebrow text-gold mb-10">04 · Speaking</div>
            <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95]">
              A voice on <em className="italic text-gold">AI</em>, business, and
              building on your own terms.
            </h2>
            <p className="mt-8 text-ivory/70 max-w-lg leading-relaxed">
              Toia speaks to conferences, universities, and private organizations on artificial
              intelligence, entrepreneurship, and the future of founder-led companies.
            </p>
            <div className="mt-10 flex flex-wrap gap-4">
              <Link to="/speaking" className="btn-secondary border-ivory text-ivory hover:bg-ivory hover:text-matte">
                Explore Speaking
              </Link>
              <Link to="/speaking#inquiry" className="btn-ghost text-ivory border-ivory hover:text-gold hover:border-gold">
                Book Toia Wallace
              </Link>
            </div>
          </div>
          <div className="lg:col-span-6">
            <div className="rounded-3xl overflow-hidden luxury-shadow">
              <img src={PHOTOS.speaking} alt="Toia Wallace speaking" className="w-full h-[70vh] object-cover" loading="lazy" />
            </div>
          </div>
        </div>
      </section>

      {/* PULL QUOTE */}
      <section className="py-24 lg:py-32 bg-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <PullQuote attribution="Toia Wallace · Founder">
            The next great companies will be built quietly, by founders who understand that
            technology, taste, and time are the real assets.
          </PullQuote>
        </div>
      </section>

      {/* FOUNDER JOURNEY */}
      <section className="py-32 lg:py-56 bg-white">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="mb-24 max-w-3xl">
            <SectionMarker number="05" label="The Founder Journey" />
            <h2 className="mt-10 font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.95] text-matte">
              A path of chapters, not <em className="italic text-taupe">a résumé.</em>
            </h2>
          </div>
          <div data-testid={HOME.timeline}>
            <Timeline />
          </div>
        </div>
      </section>

      {/* RESOURCES */}
      <section className="py-32 lg:py-56 bg-sand/40">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-20">
            <div>
              <SectionMarker number="06" label="Resources" />
              <h2 className="mt-8 font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none text-matte max-w-2xl">
                For the founders building next.
              </h2>
            </div>
            <a href={STAN_STRATEGY_URL} target="_blank" rel="noreferrer" data-testid={RESOURCES.strategyCta} className="btn-primary self-start">
              Book a Strategy Intensive
              <ArrowUpRight size={14} strokeWidth={1.6} />
            </a>
          </div>

          <div data-testid={HOME.resources} className="grid md:grid-cols-2 lg:grid-cols-5 gap-6">
            {RESOURCES_ITEMS.map((r, i) => (
              <div key={r.key} className="bg-white rounded-2xl p-8 hover:-translate-y-1 transition-transform duration-500 border border-transparent hover:border-gold/30 luxury-shadow">
                <div className="eyebrow text-taupe mb-4">{String(i + 1).padStart(2, '0')}</div>
                <h3 className="font-serif-display text-2xl text-matte mb-3 leading-tight tracking-tight">{r.label}</h3>
                <p className="text-sm text-matte/65 leading-relaxed">{r.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* AS FEATURED IN */}
      <section className="py-20 lg:py-24 bg-white border-y border-taupe/20" data-testid="home-as-featured-in">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20">
          <div className="text-center mb-12">
            <div className="eyebrow text-taupe">As Featured In</div>
          </div>
          <div className="flex flex-wrap items-center justify-center gap-x-12 gap-y-6 max-w-5xl mx-auto">
            {['VoyageMIA', 'Voyage Jacksonville', 'VoyageATL', 'Bold Journey', 'CanvasRebel', 'Shoutout Atlanta'].map((p) => (
              <span key={p} className="font-serif-display italic text-2xl md:text-3xl text-matte/70 tracking-tight">
                {p}
              </span>
            ))}
          </div>
          <div className="mt-12 flex justify-center">
            <Link to="/media" className="btn-ghost" data-testid="home-view-media-press">
              View Media &amp; Press
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          </div>
        </div>
      </section>

      {/* PUBLISHED WORKS */}
      <section className="py-32 lg:py-40 bg-sand/40" data-testid="home-published-works">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-20 grid lg:grid-cols-12 gap-12 lg:gap-20 items-center">
          <div className="lg:col-span-6">
            <SectionMarker number="07" label="Published Works" />
            <h2 className="mt-10 font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tightest leading-[0.95] text-matte">
              Faith Got Me Here.
            </h2>
            <p className="mt-6 font-serif-display italic text-2xl md:text-3xl text-taupe leading-snug max-w-xl">
              A Transparent Journey of Trust, Transformation &amp; Triumph.
            </p>
            <p className="mt-10 text-matte/70 leading-[1.85] max-w-lg">
              A memoir of faith, perseverance, entrepreneurship, and trusting God&rsquo;s timing through
              every season of building a purpose-driven life.
            </p>
            <div className="mt-10 flex flex-wrap items-center gap-4">
              <a href="https://www.amazon.com/dp/B0GHS19JH7" target="_blank" rel="noreferrer" className="btn-primary" data-testid="home-cta-buy-book">
                Buy on Amazon
                <ArrowUpRight size={14} strokeWidth={1.6} />
              </a>
              <span className="font-label text-[10px] tracking-widest uppercase text-matte/60">Published · Jan 1, 2026</span>
            </div>
          </div>
          <div className="lg:col-span-6 flex items-center justify-center">
            <img
              src="https://customer-assets-m6fa6gv7.emergentagent.net/job_toia-ecosystem/artifacts/zz137mrc_Front%20Cover.webp"
              alt="Faith Got Me Here book cover"
              className="w-full max-w-[320px] lg:max-w-[420px] h-auto rounded-md shadow-2xl"
              loading="lazy"
              data-testid="home-book-cover"
            />
          </div>
        </div>
      </section>

      {/* THE FOUNDER'S LIST — email signup */}
      <section
        id="founders-list"
        data-testid="home-founders-list"
        className="relative py-24 lg:py-40 bg-white overflow-hidden"
      >
        <div className="absolute inset-0 opacity-40 pointer-events-none">
          <div className="absolute inset-0 bg-gradient-to-b from-sand/30 via-white to-white" />
        </div>
        <div className="relative mx-auto max-w-[1240px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-12 lg:gap-20 items-start">
          <div className="lg:col-span-6">
            <div className="eyebrow text-taupe mb-6">From my desk to yours</div>
            <h2 className="font-serif-display text-4xl sm:text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-[0.98] text-matte">
              Join The
              <br />
              <em className="italic text-taupe">Founder&rsquo;s List.</em>
            </h2>
            <p className="mt-8 font-serif-display italic text-xl md:text-2xl text-taupe leading-snug max-w-md">
              Building companies isn&rsquo;t just about the destination. It&rsquo;s about the journey.
            </p>
            <div className="mt-8 space-y-5 text-matte/75 leading-[1.85] max-w-lg">
              <p>
                Get exclusive behind-the-scenes access as I build technology companies, AI platforms, and global ventures.
              </p>
              <p>
                When you join The Founder&rsquo;s List, you&rsquo;ll receive <strong className="text-matte">The Founder&rsquo;s Brief</strong> every Friday, a five-minute email sharing what I&rsquo;m building, testing, learning, and watching in real time.
              </p>
              <p>Be the first to hear about new launches, speaking engagements, resources, platforms, and opportunities.</p>
            </div>

            <div className="mt-10">
              <div className="eyebrow text-taupe mb-4">Inside The Founder&rsquo;s Brief</div>
              <ul className="grid sm:grid-cols-2 gap-x-6 gap-y-2 text-[14px] text-matte/80">
                {[
                  'What I built this week',
                  "AI tools I'm actually using",
                  "Business lessons I'm learning",
                  "Technology I'm testing",
                  'Companies and platforms I&rsquo;m building',
                  "Opportunities I'm watching",
                  'Early access to launches and resources',
                ].map((item, i) => (
                  <li key={i} className="flex items-baseline gap-3">
                    <span className="text-gold text-[10px]">&#9675;</span>
                    <span dangerouslySetInnerHTML={{ __html: item }} />
                  </li>
                ))}
              </ul>
            </div>
            <p className="mt-8 font-serif-display italic text-lg text-taupe">
              No fluff. No spam. Just one thoughtful email every Friday.
            </p>
            <p className="mt-3 text-[13px] text-matte/60">
              Join founders, entrepreneurs, and visionaries building what&rsquo;s next.
            </p>
          </div>

          <div className="lg:col-span-6 lg:sticky lg:top-32">
            <div className="bg-ivory border border-taupe/30 rounded-3xl p-6 sm:p-10 lg:p-12 luxury-shadow">
              <FoundersListForm source="homepage" variant="panel" redirectOnSuccess />
              <div className="mt-8 pt-6 border-t border-taupe/20">
                <Link to="/founders-list" className="text-[12px] uppercase tracking-[0.22em] text-taupe hover:text-matte transition-colors">
                  Read more about The Founder&rsquo;s List &rarr;
                </Link>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* FOUNDER SYSTEMS SCORECARD CTA */}
      <section className="py-20 lg:py-28 bg-matte text-ivory" data-testid="home-scorecard-cta">
        <div className="mx-auto max-w-[1240px] px-6 sm:px-10 lg:px-16 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="eyebrow text-gold mb-4">Founder Systems Scorecard<span className="align-super text-[9px]">™</span></div>
            <h2 className="font-serif-display text-4xl sm:text-5xl lg:text-6xl tracking-tight leading-[1.02]">
              How strong are the systems
              <br />
              <em className="italic text-gold">underneath your business?</em>
            </h2>
            <p className="mt-6 text-ivory/80 max-w-xl leading-relaxed">
              In less than 7 minutes, see where you&rsquo;re strong, where you&rsquo;re still the system, and where AI or automation could create more capacity.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/founder-scorecard"
              data-testid="home-scorecard-cta-btn"
              className="inline-flex items-center gap-3 bg-gold text-matte px-8 py-4 rounded-full text-[11px] tracking-[0.22em] uppercase hover:bg-ivory transition-all"
            >
              Take the Free Scorecard
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
            <div className="mt-4 text-[11px] tracking-[0.18em] uppercase text-ivory/60">28 Questions · Free · Immediate Results</div>
          </div>
        </div>
      </section>

      {/* FOUNDER JOURNAL */}
      <section className="py-24 lg:py-40 bg-ivory">
        <div className="mx-auto max-w-[1440px] px-6 sm:px-10 lg:px-16">
          <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-8 mb-16">
            <div>
              <div className="eyebrow text-taupe mb-6">From My Desk</div>
              <h2 className="font-serif-display text-5xl md:text-6xl lg:text-7xl tracking-tighter leading-none text-matte">
                Founder <em className="italic text-taupe">Journal.</em>
              </h2>
            </div>
            <Link to="/journal" data-testid={J_TID.viewAll} className="btn-ghost self-start">
              View All Founder Journal
              <ArrowRight size={14} strokeWidth={1.6} />
            </Link>
          </div>

          <div data-testid={HOME.insights} className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {latestJournal.map((entry) => (
              <Link
                key={entry.slug}
                to="/journal"
                data-testid={J_TID.card(entry.slug)}
                className="group block bg-white rounded-2xl p-8 md:p-10 luxury-shadow border border-transparent hover:border-gold/40 transition-[transform,border-color] duration-500 hover:-translate-y-1"
              >
                <div className="flex items-center justify-between mb-8">
                  <div className="eyebrow text-taupe">{entry.kicker || 'From My Desk'}</div>
                  <div className="font-label text-[10px] tracking-widest uppercase text-matte/50">{formatMonthYear(entry.published_date)}</div>
                </div>
                <h3 className="font-serif-display text-3xl md:text-4xl leading-tight tracking-tightest text-matte mb-4 group-hover:text-gold transition-colors duration-500">
                  {entry.title}
                </h3>
                <p className="text-sm text-matte/70 leading-relaxed">{entry.short_description}</p>
                <div className="mt-8 h-px bg-taupe/25 group-hover:bg-gold transition-colors duration-500" />
                <div className="mt-4 font-label text-[10px] tracking-widest uppercase text-matte/50">{entry.read_time}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
